import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import {
  fetchConversations,
  fetchConversationsCount,
  exportConversations,
  fetchConversationCountries,
} from '../api/analytics'
import type {
  ConversationsFilters,
  ExportFormat,
  ExportResponse,
} from '../types/analytics'

const PAGE_SIZE_OPTIONS = [50, 100]

/**
 * Save an export response as a file.
 *
 * CSV gets a BOM because Excel otherwise decodes the file with the local ANSI
 * codepage and turns Cyrillic text into mojibake.
 */
function downloadExport(result: ExportResponse) {
  const isCsv = result.format === 'csv'
  const parts = isCsv ? ['\uFEFF', result.content] : [result.content]
  const blob = new Blob(parts, {
    type: isCsv ? 'text/csv;charset=utf-8' : 'text/markdown;charset=utf-8',
  })

  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = result.filename || `conversations-${Date.now()}.${isCsv ? 'csv' : 'md'}`
  link.click()
  URL.revokeObjectURL(url)
}

export default function Conversations() {
  const { t } = useTranslation()
  const [filters, setFilters] = useState<ConversationsFilters>({})
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0])
  const [offset, setOffset] = useState(0)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [exportFormat, setExportFormat] = useState<ExportFormat>('markdown')
  const [isExporting, setIsExporting] = useState(false)

  // Fetch conversations with TanStack Query
  const { data: conversations = [], isLoading, error, refetch } = useQuery({
    queryKey: ['conversations', filters, pageSize, offset],
    queryFn: () => fetchConversations({ ...filters, limit: pageSize, offset }),
  })

  // Total for the current filter - drives the page range and tells the operator
  // how many conversations a filter-wide export will contain.
  const { data: countData } = useQuery({
    queryKey: ['conversations-count', filters],
    queryFn: () => fetchConversationsCount(filters),
  })

  const { data: countries = [] } = useQuery({
    queryKey: ['countries', filters.site_code],
    queryFn: () => fetchConversationCountries(filters.site_code),
  })

  const total = countData?.total ?? 0
  const hasSelection = selectedIds.size > 0
  const exportCount = hasSelection ? selectedIds.size : total

  // Checkboxes mark rows on the current screen. Carrying them across a filter or
  // page change would export rows the operator can no longer see.
  useEffect(() => {
    setSelectedIds(new Set())
  }, [filters, pageSize, offset])

  const handleExport = async () => {
    if (exportCount === 0) {
      alert(t('conversations.export.nothingToExport'))
      return
    }

    setIsExporting(true)

    try {
      // Marked rows win; with nothing marked the whole filter is exported, which
      // is what makes a 450-conversation download a single click.
      const result = await exportConversations({
        ...(hasSelection ? { conversation_ids: Array.from(selectedIds) } : { filters }),
        format: exportFormat,
        include_metadata: true,
      })

      downloadExport(result)

      alert(t('conversations.export.success', { total: result.count }))
      setSelectedIds(new Set())
    } catch (err) {
      alert(`${t('conversations.export.error')}: ${err instanceof Error ? err.message : t('common.unknownError')}`)
    } finally {
      setIsExporting(false)
    }
  }

  const updateFilters = (patch: Partial<ConversationsFilters>) => {
    setFilters((prev) => {
      const next = { ...prev, ...patch }
      // An `undefined` value means "filter cleared", so drop the key entirely.
      for (const key of Object.keys(patch) as (keyof ConversationsFilters)[]) {
        if (next[key] === undefined) {
          delete next[key]
        }
      }
      return next
    })
    // A narrower filter usually has fewer pages than the one being replaced.
    setOffset(0)
  }

  const toggleSelection = (id: number) => {
    const newSelection = new Set(selectedIds)
    if (newSelection.has(id)) {
      newSelection.delete(id)
    } else {
      newSelection.add(id)
    }
    setSelectedIds(newSelection)
  }

  const selectAll = () => {
    if (selectedIds.size === conversations.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(conversations.map((c) => c.id)))
    }
  }

  if (error) {
    return (
      <div className="bg-rose-500/10 border border-rose-500/40 rounded-xl p-4">
        <h3 className="text-rose-200 font-medium">{t('conversations.errorLoading')}</h3>
        <p className="text-rose-300 text-sm mt-1">
          {error instanceof Error ? error.message : t('common.unknownError')}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6 overflow-x-hidden w-full max-w-full">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-semibold text-white">{t('conversations.title')}</h2>

        <div className="flex gap-3">
          <select
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
            aria-label={t('conversations.export.format')}
            className="h-11 px-3 py-2 rounded-lg border border-slate-700 bg-surface-300 text-slate-100 focus:ring-2 focus:ring-primary-500/60 focus:border-primary-400/70 transition"
          >
            <option value="markdown">{t('conversations.export.formatMarkdown')}</option>
            <option value="csv">{t('conversations.export.formatCsv')}</option>
          </select>
          <button
            onClick={handleExport}
            disabled={exportCount === 0 || isExporting}
            title={
              hasSelection
                ? t('conversations.export.hintSelected')
                : t('conversations.export.hintFiltered')
            }
            className="px-4 py-2 bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 rounded-lg hover:bg-emerald-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-emerald-500/20"
          >
            {isExporting
              ? t('conversations.export.inProgress')
              : hasSelection
                ? t('conversations.export.buttonSelected', { total: selectedIds.size })
                : t('conversations.export.buttonFiltered', { total })}
          </button>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 rounded-lg bg-primary-500/20 border border-primary-500/50 text-primary-100 hover:bg-primary-500/30 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-slate-800 bg-surface-200/80 backdrop-blur p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs uppercase tracking-wide text-muted-300 mb-2">
              {t('conversations.filters.site')}
            </label>
            <select
              value={filters.site_code || ''}
              onChange={(e) =>
                updateFilters({ site_code: e.target.value || undefined })
              }
              className="w-full h-11 px-3 py-2 rounded-md border border-slate-700 bg-surface-300 text-slate-100 placeholder-slate-500 focus:ring-2 focus:ring-primary-500/60 focus:border-primary-400/70 transition"
            >
              <option value="">{t('conversations.filters.allSites')}</option>
              <option value="ua">{t('dashboard.filters.ukraine')}</option>
              <option value="ge">{t('dashboard.filters.georgia')}</option>
              <option value="wp">{t('dashboard.filters.wordpress')}</option>
            </select>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wide text-muted-300 mb-2">
              {t('conversations.filters.email')}
            </label>
            <input
              type="text"
              value={filters.email || ''}
              onChange={(e) =>
                updateFilters({ email: e.target.value || undefined })
              }
              placeholder={t('conversations.filters.email')}
              className="w-full h-11 px-3 py-2 rounded-md border border-slate-700 bg-surface-300 text-slate-100 placeholder-slate-500 focus:ring-2 focus:ring-primary-500/60 focus:border-primary-400/70 transition"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wide text-muted-300 mb-2">
              {t('conversations.filters.country')}
            </label>
            <select
              value={filters.country_code || ''}
              onChange={(e) =>
                updateFilters({ country_code: e.target.value || undefined })
              }
              className="w-full h-11 px-3 py-2 rounded-md border border-slate-700 bg-surface-300 text-slate-100 placeholder-slate-500 focus:ring-2 focus:ring-primary-500/60 focus:border-primary-400/70 transition"
            >
              <option value="">{t('conversations.filters.allCountries')}</option>
              {countries.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name} ({country.code})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wide text-muted-300 mb-2">
              {t('conversations.filters.dateFrom')}
            </label>
            <input
              type="date"
              value={filters.start_date || ''}
              onChange={(e) =>
                updateFilters({ start_date: e.target.value || undefined })
              }
              className="w-full px-3 py-2 rounded-md border border-slate-700 bg-surface-300 text-slate-100 placeholder-slate-500 focus:ring-2 focus:ring-primary-500/60 focus:border-primary-400/70 transition"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wide text-muted-300 mb-2">
              {t('conversations.filters.dateTo')}
            </label>
            <input
              type="date"
              value={filters.end_date || ''}
              onChange={(e) =>
                updateFilters({ end_date: e.target.value || undefined })
              }
              className="w-full px-3 py-2 rounded-md border border-slate-700 bg-surface-300 text-slate-100 placeholder-slate-500 focus:ring-2 focus:ring-primary-500/60 focus:border-primary-400/70 transition"
            />
          </div>
          <div className="lg:col-span-5">
            <div className="block text-xs uppercase tracking-wide text-muted-300 mb-2">
              {t('conversations.filters.flagged')} & {t('conversations.filters.feedback')}
            </div>
            <div className="flex flex-wrap gap-4">
              <label className="inline-flex items-center gap-2 text-sm text-slate-200">
                <input
                  type="checkbox"
                  checked={filters.flagged === true}
                  onChange={(e) =>
                    updateFilters({ flagged: e.target.checked ? true : undefined })
                  }
                  className="rounded border-slate-700 bg-surface-300 text-amber-300 focus:ring-amber-400/70"
                />
                {t('conversations.filters.onlyFlagged')}
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-200">
                <input
                  type="checkbox"
                  checked={filters.has_like === true}
                  onChange={(e) =>
                    updateFilters({ has_like: e.target.checked ? true : undefined })
                  }
                  className="rounded border-slate-700 bg-surface-300 text-emerald-300 focus:ring-emerald-400/70"
                />
                {t('conversations.filters.onlyLikes')}
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-200">
                <input
                  type="checkbox"
                  checked={filters.has_dislike === true}
                  onChange={(e) =>
                    updateFilters({ has_dislike: e.target.checked ? true : undefined })
                  }
                  className="rounded border-slate-700 bg-surface-300 text-rose-300 focus:ring-rose-400/70"
                />
                {t('conversations.filters.onlyDislikes')}
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-slate-800 bg-surface-200/80 backdrop-blur overflow-hidden w-full">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-slate-400">{t('conversations.loading')}</div>
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-slate-400">{t('conversations.noConversations')}</div>
          </div>
        ) : (
          <div className="overflow-x-auto w-full">
            <table className="w-full divide-y divide-slate-800">
              <thead className="bg-surface-200/60">
                <tr>
                  <th className="px-3 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === conversations.length}
                      onChange={selectAll}
                      className="rounded border-slate-700 bg-surface-300 text-primary-400 focus:ring-primary-400/70"
                    />
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-muted-200 uppercase tracking-wider">
                    {t('conversations.table.site')}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-muted-200 uppercase tracking-wider">
                    {t('conversations.table.email')}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-muted-200 uppercase tracking-wider">
                    {t('conversations.table.country')}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-muted-200 uppercase tracking-wider">
                    {t('conversations.table.tokens')}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-muted-200 uppercase tracking-wider">
                    Cost
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-muted-200 uppercase tracking-wider">
                    {t('conversations.table.created')}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-muted-200 uppercase tracking-wider">
                    {t('conversations.table.flagged')}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-muted-200 uppercase tracking-wider">
                    Feedback
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-muted-200 uppercase tracking-wider">
                    {t('conversations.table.status')}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-muted-200 uppercase tracking-wider sticky right-0 bg-surface-200/60 backdrop-blur">
                    {t('conversations.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-transparent divide-y divide-slate-800">
                {conversations.map((conv) => (
                  <tr key={conv.id} className="hover:bg-slate-900/40 transition">
                    <td className="px-3 py-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(conv.id)}
                        onChange={() => toggleSelection(conv.id)}
                        className="rounded border-slate-700 bg-surface-300 text-primary-400 focus:ring-primary-400/70"
                      />
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-slate-200">
                      <span className="px-2 py-1 bg-primary-500/20 text-primary-100 rounded text-xs font-medium">
                        {conv.site_code.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-slate-300">
                      {conv.email ? (
                        <span className="truncate block max-w-[200px]" title={conv.email}>
                          {conv.email}
                        </span>
                      ) : conv.email_hash ? (
                        <span className="truncate block max-w-[140px]" title={conv.email_hash}>
                          {conv.email_hash.slice(0, 12)}...
                        </span>
                      ) : (
                        <span className="text-slate-600">-</span>
                      )}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-slate-300">
                      {conv.country_name || '-'}
                      {conv.country_code && ` (${conv.country_code})`}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-slate-300">
                      {conv.token_usage_in || 0} / {conv.token_usage_out || 0}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-slate-300">
                      {typeof conv.token_cost_usd === 'number'
                        ? `$${conv.token_cost_usd.toFixed(4)}`
                        : '-'}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-slate-300">
                      {format(new Date(conv.created_at), 'MMM dd, HH:mm')}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-slate-300">
                      {conv.flagged ? (
                        <span
                          className="px-2 py-1 bg-amber-500/10 text-amber-200 border border-amber-500/40 rounded text-xs font-medium"
                          title={conv.flagged_reason || 'Content flagged by moderation'}
                        >
                          🚨 {t('conversations.table.flagged')}
                        </span>
                      ) : (
                        <span className="text-slate-500 text-xs">-</span>
                      )}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-slate-300">
                      {conv.like_count === 0 && conv.dislike_count === 0 ? (
                        <span className="text-slate-600">-</span>
                      ) : (
                        <div className="flex gap-2">
                          {conv.like_count > 0 && (
                            <span className="px-2 py-1 bg-emerald-500/10 text-emerald-200 border border-emerald-500/40 rounded text-xs font-medium">
                              👍 {conv.like_count}
                            </span>
                          )}
                          {conv.dislike_count > 0 && (
                            <span className="px-2 py-1 bg-rose-500/10 text-rose-200 border border-rose-500/40 rounded text-xs font-medium">
                              👎 {conv.dislike_count}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="flex gap-2 items-center">
                        {conv.error_code ? (
                          <span className="px-2 py-1 bg-rose-500/10 text-rose-200 border border-rose-500/40 rounded text-xs font-medium">
                            {t('conversations.table.statusError')}
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-emerald-500/10 text-emerald-200 border border-emerald-500/30 rounded text-xs font-medium">
                            {t('conversations.table.statusOk')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm font-medium sticky right-0 bg-surface-200/60 backdrop-blur">
                      <Link
                        to={`/conversations/${conv.id}`}
                        className="text-primary-200 hover:text-primary-100"
                      >
                        {t('conversations.table.view')}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-slate-800 bg-surface-200/80 backdrop-blur px-6 py-4">
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-300">
            {t('conversations.pagination.pageSize')}
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value))
                setOffset(0)
              }}
              className="h-9 px-2 py-1 rounded-md border border-slate-700 bg-surface-300 text-slate-100 focus:ring-2 focus:ring-primary-500/60 focus:border-primary-400/70 transition"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
          <div className="text-sm text-slate-300">
            {t('conversations.pagination.showing', {
              from: conversations.length === 0 ? 0 : offset + 1,
              to: offset + conversations.length,
              total,
            })}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setOffset(Math.max(0, offset - pageSize))}
            disabled={offset === 0}
            className="px-4 py-2 bg-surface-300 border border-slate-700 rounded-md text-sm font-medium text-slate-200 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('conversations.pagination.previous')}
          </button>
          <button
            onClick={() => setOffset(offset + pageSize)}
            disabled={offset + pageSize >= total}
            className="px-4 py-2 bg-surface-300 border border-slate-700 rounded-md text-sm font-medium text-slate-200 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('conversations.pagination.next')}
          </button>
        </div>
      </div>
    </div>
  )
}
